import { IndianRupee, TrendingUp, Clock, Check } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  CompactStatCard,
  DataTable,
  PageHeader,
  StatsGrid,
  useDataTable,
  type DataTableColumn,
} from "@/components/shared";
import { useStoreDashboard, useStoreEarnings, useStoreId } from "@/hooks/queries";
import type { StoreEarningRow } from "@/types/store.ui";

const statusBadge: Record<string, string> = {
  paid: "bg-success/10 text-success border-success/20",
  pending: "bg-warning/10 text-warning border-warning/20",
  processing: "bg-info/10 text-info border-info/20",
};

const StoreEarnings = () => {
  const { storeId, isLoading: storeLoading } = useStoreId();
  const { data: stats, isLoading: statsLoading } = useStoreDashboard(storeId);
  const { data: earningsData, isLoading: earningsLoading } = useStoreEarnings(storeId);

  const earnings = earningsData?.items ?? [];

  const table = useDataTable<StoreEarningRow>({
    data: earnings,
    searchKeys: ["period"],
  });

  const columns: DataTableColumn<StoreEarningRow>[] = [
    {
      id: "period",
      header: "Period",
      accessorKey: "period",
      sortable: true,
      mobilePriority: 1,
      cell: (e) => <span className="font-semibold">{e.period}</span>,
    },
    {
      id: "totalSales",
      header: "Sales",
      accessorKey: "totalSales",
      sortable: true,
      align: "right",
      mobileLabel: "Sales",
      mobilePriority: 2,
      cell: (e) => `₹${e.totalSales.toLocaleString()}`,
    },
    {
      id: "commission",
      header: "Commission",
      accessorKey: "commission",
      sortable: true,
      align: "right",
      hideOnMobile: true,
      cell: (e) => <span className="text-destructive">-₹{e.commission.toLocaleString()}</span>,
    },
    {
      id: "netEarning",
      header: "Net Earning",
      accessorKey: "netEarning",
      sortable: true,
      align: "right",
      mobileLabel: "Net",
      mobilePriority: 3,
      cell: (e) => <span className="font-semibold text-success">₹{e.netEarning.toLocaleString()}</span>,
    },
    {
      id: "orders",
      header: "Orders",
      accessorKey: "orders",
      sortable: true,
      align: "right",
      hideOnMobile: true,
    },
    {
      id: "status",
      header: "Status",
      accessorKey: "status",
      sortable: true,
      mobileLabel: "Status",
      mobilePriority: 4,
      cell: (e) => (
        <Badge variant="outline" className={statusBadge[e.status]}>{e.status}</Badge>
      ),
    },
    {
      id: "paidDate",
      header: "Paid Date",
      accessorKey: "paidDate",
      hideOnMobile: true,
      className: "text-sm text-muted-foreground",
      cell: (e) => e.paidDate || "—",
    },
  ];

  if (storeLoading || statsLoading || earningsLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-48" />
        <div className="grid gap-4 sm:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-24 rounded-xl" />
          ))}
        </div>
        <Skeleton className="h-96 rounded-xl" />
      </div>
    );
  }

  if (!storeId || !stats) {
    return (
      <PageHeader title="Earnings" description="No store linked to your account." />
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Earnings"
        description="Track your revenue and payout history."
      />

      <StatsGrid>
        <CompactStatCard title="Total Revenue" value={`₹${(stats.totalRevenue / 1000).toFixed(0)}K`} icon={TrendingUp} iconClassName="bg-gradient-primary" />
        <CompactStatCard title="Total Orders" value={stats.totalOrders.toLocaleString()} icon={Check} />
        <CompactStatCard title="Pending Payout" value={`₹${stats.pendingSettlement.toLocaleString()}`} icon={Clock} />
        <CompactStatCard title="Total Earnings" value={`₹${stats.totalEarnings.toLocaleString()}`} icon={IndianRupee} />
      </StatsGrid>

      <DataTable
        title="Settlement History"
        columns={columns}
        data={table.rows}
        getRowId={(row) => row.id}
        search={table.state.search}
        onSearchChange={table.setSearch}
        searchPlaceholder="Search periods..."
        sort={table.state.sort}
        onSortChange={table.toggleSort}
        pagination={table.pagination}
        onPageChange={table.setPage}
        onPageSizeChange={table.setPageSize}
        renderMobileCard={(e) => (
          <div className="rounded-lg border border-border p-4 space-y-2">
            <div className="flex items-start justify-between">
              <p className="font-semibold text-sm">{e.period}</p>
              <Badge variant="outline" className={statusBadge[e.status]}>{e.status}</Badge>
            </div>
            <div className="grid grid-cols-3 gap-2 text-center text-xs">
              <div className="rounded-md bg-secondary p-2">
                <p className="font-bold">₹{e.totalSales.toLocaleString()}</p>
                <p className="text-muted-foreground">Sales</p>
              </div>
              <div className="rounded-md bg-secondary p-2">
                <p className="font-bold text-destructive">-₹{e.commission.toLocaleString()}</p>
                <p className="text-muted-foreground">Commission</p>
              </div>
              <div className="rounded-md bg-secondary p-2">
                <p className="font-bold text-success">₹{e.netEarning.toLocaleString()}</p>
                <p className="text-muted-foreground">Net</p>
              </div>
            </div>
            <p className="text-xs text-muted-foreground">{e.orders} orders{e.paidDate ? ` • Paid: ${e.paidDate}` : ""}</p>
          </div>
        )}
      />
    </div>
  );
};

export default StoreEarnings;
